import type { GenericRecordData } from '@lemonade-hq/maschema-schema';
import { useI18nContext } from '@lemonade-hq/nuiza';
import type { UniclientSummary } from '@lemonade-hq/uniclient-chat';
import { isNativeApp } from '@lemonade-hq/uniclient-mobile-bridge';
import type { DrawerSnapPoints } from '@lemonade-hq/uniclient-theme';
import { Flex, SnapDrawer, spacing } from '@lemonade-hq/uniclient-theme';
import { Price } from '../adapters/components/Price/Price';
import { MOBILE_BOTTOM_SAFE_AREA } from './layout';
import type { WizardOnAction } from './types';

const getBaseContentHeight = (variant: UniclientSummary['drawerSize']): number => {
  if (variant === 'small') {
    return 52;
  }

  if (variant === 'large') {
    return 110;
  }

  return 88;
};

const getSnapPoints = (summary: UniclientSummary, paddingBottom: number): DrawerSnapPoints => {
  switch (summary.type) {
    case 'price': {
      let contentHeight = getBaseContentHeight(summary.drawerSize);

      if (summary.previousPrice != null && summary.discount != null) {
        const additionalHeight = 22;
        if (additionalHeight > paddingBottom) {
          contentHeight += additionalHeight;
        }
      }

      return [`${contentHeight}px`, '*'];
    }

    default:
      return [];
  }
};

const getMobilePaddingBlockStart = (summary: UniclientSummary, isDrawerOpen: boolean): number | string => {
  if (summary.drawerSize === 'small') {
    return spacing.s06;
  }

  return isDrawerOpen ? spacing.s08 : 0;
};

const DRAWER_PADDING_BLOCK_END = isNativeApp() ? `${MOBILE_BOTTOM_SAFE_AREA}px` : spacing.s24;

interface WizardSummaryDrawerProps {
  readonly answers?: GenericRecordData;
  readonly disabled?: boolean;
  readonly hidden: boolean;
  readonly loading?: boolean;
  readonly open: boolean;
  readonly summary: UniclientSummary;
  readonly externalClassName?: string;
}

export const WizardSummaryDrawer: React.FC<WizardOnAction & WizardSummaryDrawerProps> = ({
  answers,
  disabled,
  hidden,
  loading,
  onAction,
  open,
  summary,
  externalClassName,
}) => {
  const { s } = useI18nContext();

  return (
    <SnapDrawer
      collapseOnScroll
      description={s('wizard_summary.price.description')}
      externalClassName={externalClassName}
      hidden={hidden}
      key={`${summary.drawerSize}-${summary.type}`}
      noExpansion={summary.noExpansion}
      open={open}
      paddingBottom={MOBILE_BOTTOM_SAFE_AREA}
      snapPoints={getSnapPoints(summary, MOBILE_BOTTOM_SAFE_AREA)}
      title={s('wizard_summary.price.title')}
    >
      {(currentSnapIndex, contentRef) => (
        <Flex
          aria-label="summary card"
          flexDirection={currentSnapIndex > 0 ? 'column' : 'row'}
          gap={spacing.s24}
          justifyContent="space-between"
          paddingBlockEnd={DRAWER_PADDING_BLOCK_END}
          paddingBlockStart={{ sm: getMobilePaddingBlockStart(summary, currentSnapIndex > 0), lg: 0 }}
          paddingInline={spacing.s08}
          ref={contentRef}
        >
          <Price
            {...summary}
            answers={answers}
            buttonSize="xs"
            disabled={disabled}
            hideBenefits={currentSnapIndex === 0}
            loading={loading}
            onAction={onAction}
            showButtonNextToPrice={currentSnapIndex === 0}
            variant="tiny"
          />
        </Flex>
      )}
    </SnapDrawer>
  );
};
